import React, { useState } from "react";
import { Field, ProtectedTag } from "../../components/kit";
import { FIELD_LABELS, useTaxonomy } from "../../lib/api";

const EMPTY = { title: "", summary: "", category_id: "", area_id: "", private: { full_name: "", phone: "", email: "", address: "", notes: "" } };

export function RequestForm({ initial, submitLabel, onSubmit, onCancel }: { initial?: any; submitLabel: string; onSubmit: (v: any) => Promise<void>; onCancel?: () => void }) {
  const { categories, areas } = useTaxonomy();
  const [v, setV] = useState<any>(() => ({ ...EMPTY, ...(initial || {}), private: { ...EMPTY.private, ...(initial?.private || {}) } }));
  const [busy, setBusy] = useState(false);
  const set = (k: string, val: string) => setV((s: any) => ({ ...s, [k]: val }));
  const setP = (k: string, val: string) => setV((s: any) => ({ ...s, private: { ...s.private, [k]: val } }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(v); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card space-y-5" data-testid="request-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Trade" id="rf-category">
          <select id="rf-category" required className="input" value={v.category_id} onChange={(e) => set("category_id", e.target.value)} data-testid="request-category-select">
            <option value="">Choose a trade</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Service area" id="rf-area">
          <select id="rf-area" required className="input" value={v.area_id} onChange={(e) => set("area_id", e.target.value)} data-testid="request-area-select">
            <option value="">Choose an area</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Short title (visible to matched vendors after consent)" id="rf-title" hint="Do not include personal details here.">
        <input id="rf-title" required minLength={3} maxLength={120} className="input" value={v.title} onChange={(e) => set("title", e.target.value)} data-testid="request-title-input" />
      </Field>
      <Field label="Job summary (non-private)" id="rf-summary" hint="Describe the work only — no names, phone numbers or addresses.">
        <textarea id="rf-summary" rows={3} maxLength={1000} className="input" value={v.summary} onChange={(e) => set("summary", e.target.value)} data-testid="request-summary-input" />
      </Field>
      <fieldset className="protected-zone space-y-4">
        <legend className="px-1"><ProtectedTag /></legend>
        <p className="text-xs text-[var(--red)]">These fields are withheld from every vendor until you approve a named recipient for specific fields.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {["full_name", "phone", "email", "address"].map((f) => (
            <Field key={f} label={FIELD_LABELS[f]} id={`rf-${f}`}>
              <input id={`rf-${f}`} className="input" value={v.private[f] || ""} onChange={(e) => setP(f, e.target.value)} data-testid={`request-private-${f}-input`} />
            </Field>
          ))}
        </div>
        <Field label={FIELD_LABELS.notes} id="rf-notes">
          <textarea id="rf-notes" rows={2} className="input" value={v.private.notes || ""} onChange={(e) => setP("notes", e.target.value)} data-testid="request-private-notes-input" />
        </Field>
      </fieldset>
      <div className="flex flex-wrap gap-3">
        <button type="submit" className="btn btn-primary" disabled={busy || categories.length === 0 || areas.length === 0} data-testid="request-form-submit-button">{busy ? "Saving…" : categories.length === 0 ? "Loading…" : submitLabel}</button>
        {onCancel && <button type="button" className="btn btn-ghost" onClick={onCancel} data-testid="request-form-cancel-button">Cancel</button>}
      </div>
    </form>
  );
}
